import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcJpmamV1NetOrderQueryServiceNetOrderGet } from '@interxfi/api-client/generated/hooks/exc-jpmam-v1-net-order-query-service-net-order-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcJpmamV1NetOrderState } from '@interxfi/types';
import { JpmamNetOrderEntityMenu } from '../../../../../../../../../../features/vendor/jpmam/net-order/jpmam-net-order-entity-menu';
import { JpmamNetOrderEventSummary } from '../../../../../../../../../../features/vendor/jpmam/net-order/jpmam-net-order-event';
import { getJpmamNetOrderStateListItems } from '../../../../../../../../../../features/vendor/jpmam/net-order/jpmam-net-order-list-items';
import { getJpmamNetOrderDetailPath } from '../../../../../../../../../../features/vendor/jpmam/net-order/jpmam-net-order-routing';
import { JpmamNetOrderStatusPill } from '../../../../../../../../../../features/vendor/jpmam/net-order/jpmam-net-order-status-pill';
import { JpmamNetOrderSummaryBar } from '../../../../../../../../../../features/vendor/jpmam/net-order/jpmam-net-order-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/jpm/net-order/_net-order/$netOrderId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `jpmam-net-order-${params.netOrderId}`,
        label: 'Net Order',
        to: getJpmamNetOrderDetailPath(params.netOrderId),
        suffix: (
          <CopyButton content={params.netOrderId} label="Copy Net Order ID" tooltip={{ copy: params.netOrderId, copied: 'Copied Net Order ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('JPMAM Net Order'),
  component: JpmamNetOrderDetail,
});

function getListItems(data: ExcJpmamV1NetOrderState | undefined) {
  const items = getJpmamNetOrderStateListItems(data);

  return {
    left: [
      items.data.purchaseQuantity,
      items.data.redemptionQuantity,
      items.data.unit,
      items.data.confirmedNav,
      items.data.confirmedShares,
      items.data.confirmedAmount,
      items.data.cusip,
      items.data.symbol,
      ...(items.data.shareClassNumber ? [items.data.shareClassNumber] : []),
    ] as const satisfies ReadonlyListItem[],
    right: [
      items.netOrderId,
      items.assetId,
      items.data.source,
      items.data.jpmReferenceNumber,
      items.data.submittedTimestamp,
      items.data.paymentSentTimestamp,
      items.data.tradeDate,
      items.data.settlementDate,
      ...(items.data.groupingId ? [items.data.groupingId] : []),
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load JPMAM net order', 'view this JPMAM net order');

function JpmamNetOrderDetail() {
  const { netOrderId } = Route.useParams();
  const request = { netOrderId } as const;
  const { data, error, isPending } = useExcJpmamV1NetOrderQueryServiceNetOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.netOrder), [data?.netOrder]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <JpmamNetOrderStatusPill isPending={showPending} size="large" status={data?.netOrder?.status} />
            </HorizontalStack>

            <JpmamNetOrderEntityMenu {...request} />
          </PageHeader>

          <JpmamNetOrderSummaryBar {...request} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <JpmamNetOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
